import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtUSD } from "../lib/api";
import { Users, ChevronRight, AlertCircle } from "lucide-react";

const BUSINESS_LABEL = {
  jk_repair: "Repair",
  jk_trucking: "Trucking",
};

/** Dashboard card mirroring PartnerOutstandingCard, but for J&K Repair /
 *  J&K Trucking customer invoices. */
export default function CustomerOutstandingCard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/customer-outstanding").then((r) => setData(r.data)).catch(() => setData(null));
  }, []);

  if (!data || !data.customers || data.customers.length === 0) return null;
  const owed = data.customers.filter((c) => c.outstanding > 0.005);
  const total = data.totals.outstanding;
  if (owed.length === 0) return null;

  const linkFor = (c) => {
    // If they billed under one business, deep-link there. Otherwise send them to Hub.
    if (c.businesses?.length === 1) {
      return c.businesses[0] === "jk_repair" ? "/repair/invoices" : "/trucking/invoices";
    }
    return "/hub";
  };

  return (
    <div className="bg-white border-2 border-[#1F1E1B] mb-6" data-testid="customer-outstanding-card">
      <div className="bg-[#1A4135] text-white px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4" />
          <span className="text-[11px] font-bold uppercase tracking-[0.2em]">Owed by Customers</span>
        </div>
        <Link to="/hub" data-testid="customer-outstanding-link" className="text-[10px] uppercase tracking-wider hover:underline flex items-center gap-1">
          All invoices <ChevronRight className="w-3 h-3" />
        </Link>
      </div>
      <div className="p-4">
        <div className="flex items-baseline gap-3 mb-3">
          <div className="font-display text-4xl font-black tabular text-[#BC4727]">{fmtUSD(total)}</div>
          <div className="text-xs text-[#66645E]">across {owed.length} customer{owed.length === 1 ? "" : "s"}</div>
        </div>
        <ul className="divide-y divide-[#E5E2D6]">
          {owed.slice(0, 4).map((c) => (
            <li key={c.customer_id} className="flex items-center justify-between py-2" data-testid={`customer-outstanding-row-${c.customer_id}`}>
              <div>
                <div className="font-semibold text-sm">{c.customer_name}</div>
                <div className="text-[10px] text-[#66645E] uppercase tracking-wider">
                  {c.open_invoices} open · billed {fmtUSD(c.billed)}, paid {fmtUSD(c.paid)}
                  {c.businesses?.length ? ` · ${c.businesses.map((b) => BUSINESS_LABEL[b] || b).join(" + ")}` : ""}
                </div>
              </div>
              <div className="text-right">
                <div className="font-bold tabular text-[#BC4727]">{fmtUSD(c.outstanding)}</div>
                <Link
                  to={linkFor(c)}
                  data-testid={`customer-outstanding-view-${c.customer_id}`}
                  className="text-[10px] uppercase tracking-wider text-[#1A4135] hover:underline"
                >
                  View invoices →
                </Link>
              </div>
            </li>
          ))}
        </ul>
        {owed.length > 4 && (
          <div className="text-[10px] text-[#66645E] uppercase tracking-wider mt-2 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" /> {owed.length - 4} more with open balances
          </div>
        )}
      </div>
    </div>
  );
}
